import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  Expense,
  ExpenseCategory,
  ExpenseStatus,
  ExpenseSummaryResponse,
  UpdateExpenseStatusResponse,
} from '../../expense.model';
import { EmployeesService } from '../../../employees/employees.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { Employee, EmployeeBasicInfo } from '../../../employees/employees.model';
import { ExpensesService } from '../../expenses.service';
import { buildFilterQuery, isObjectEmpty } from '../../../utils';

@Component({
  selector: 'app-expenses',
  standalone: false,
  templateUrl: './expenses.component.html',
  styleUrl: './expenses.component.scss',
})
export class ExpensesComponent implements OnInit, OnDestroy {
  @ViewChild('expenseDetailsModal') expenseDetailsModal!: TemplateRef<any>;
  @ViewChild('expenseActionModal') expenseActionModalRef!: TemplateRef<any>;

  protected modalRef!: NgbModalRef;
  protected employeeList: EmployeeBasicInfo[] = [];
  protected expensesList: Expense[] = [];
  protected selectedExpense: Expense | null = null;
  protected expensesSummary: ExpenseSummaryResponse | null = null;
  protected isApplyingFilter: boolean = false;
  protected isDataLoading: boolean = true;
  protected isExportingExpenses: boolean = false;
  protected exportFilters = {
    employee_id: null,
    from_date: '',
    to_date: '',
  };

  protected statusOptions = [
    { label: 'Pending', value: ExpenseStatus.PENDING },
    { label: 'Approved', value: ExpenseStatus.APPROVED },
    { label: 'Rejected', value: ExpenseStatus.REJECTED },
  ];

  protected categoryOptions = [
    { label: 'Travel', value: ExpenseCategory.TRAVEL },
    { label: 'Food', value: ExpenseCategory.FOOD },
    { label: 'Materials', value: ExpenseCategory.MATERIALS },
    { label: 'Other', value: ExpenseCategory.OTHER },
  ];

  protected filters = {
    employee: null,
    status: null,
    category: null,
    from_date: '',
    to_date: '',
  };

  protected summaryCards = [
    {
      label: 'Pending Expenses',
      icon: 'bi-hourglass-split',
      iconColor: 'warning',
      borderColor: 'warning',
      key: ExpenseStatus.PENDING,
    },
    {
      label: 'Approved Expenses',
      icon: 'bi-check-circle-fill',
      iconColor: 'success',
      borderColor: 'success',
      key: ExpenseStatus.APPROVED,
    },
    {
      label: 'Rejected Expenses',
      icon: 'bi-x-circle-fill',
      iconColor: 'danger',
      borderColor: 'danger',
      key: ExpenseStatus.REJECTED,
    },
  ];

  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;

  private _expensesSubscription!: Subscription;
  protected currentPage: number = 1;

  protected readonly ExpenseStatus = ExpenseStatus;

  constructor(
    private _modalService: NgbModal,
    private _employeeService: EmployeesService,
    private _expensesService: ExpensesService,
    private _changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._getExpensesData();
  }

  // protected methods

  protected applyFilters(): void {
    this.currentPage = 1;

    const queryParams = buildFilterQuery({
      ...this.filters,
      page: this.currentPage,
    });

    this.isApplyingFilter = true;

    this._expensesSubscription = this._expensesService
      .list(queryParams)
      .pipe(finalize(() => (this.isApplyingFilter = false)))
      .subscribe({
        next: expensesDetails => {
          this.expensesList = expensesDetails.results || [];
          this.hasPreviousPage = !!expensesDetails.next;
          this.hasNextPage = !!expensesDetails.previous;
          this.totalCount = expensesDetails.count;
          this.expensesSummary = expensesDetails.summary ?? null;
          this._changeDetectorRef.detectChanges();
        },
      });
  }

  onViewExpense(expense: any): void {
    this.selectedExpense = expense;
    this._modalService.open(this.expenseDetailsModal, { size: 'lg' });
  }

  openExpenseActionModal(expense: Expense) {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'md',
    };

    this.selectedExpense = expense;
    this._openModal(this.expenseActionModalRef, modalOptions);
  }

  protected confirmExpenseAction(status: ExpenseStatus, modalRef: NgbModalRef) {
    if (!this.selectedExpense) return;

    const reqBody = {
      status: status,
    };

    this._expensesSubscription = this._expensesService.update(this.selectedExpense.id, reqBody).subscribe({
      next: (updatedExpenseStatus: UpdateExpenseStatusResponse) => {
        this._getExpensesData();
        modalRef.close();
        this.selectedExpense = null;
      },
    });
  }

  protected onExportExpenses(): void {
    this.isExportingExpenses = true;

    const { employee_id, from_date, to_date } = this.exportFilters;
    const filename =
      from_date && to_date
        ? `expenses_${from_date}_to_${to_date}.xlsx`
        : from_date
          ? `expenses_from_${from_date}.xlsx`
          : 'expenses.xlsx';

    this._expensesService
      .export({
        employee_id: employee_id || undefined,
        from_date: from_date || undefined,
        to_date: to_date || undefined,
      })
      .pipe(finalize(() => (this.isExportingExpenses = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  // private methods

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this._modalService.open(content, options);
  }

  private _getExpensesData(): void {
    this.isDataLoading = true;

    const employee$ = this._employeeService.lighter_list();
    const expenses$ = this._expensesService.list({ page: this.currentPage });

    this._expensesSubscription = forkJoin([employee$, expenses$])
      .pipe(finalize(() => (this.isDataLoading = false)))
      .subscribe({
        next: ([employeeDetails, expensesDetails]) => {
          this.employeeList = employeeDetails || [];
          this.expensesList = expensesDetails.results || [];
          this.totalCount = expensesDetails.count;
          this.hasNextPage = !!expensesDetails.next;
          this.hasPreviousPage = !!expensesDetails.previous;
          this.expensesSummary = expensesDetails.summary ?? null;
        },
      });
  }

  ngOnDestroy(): void {
    this.modalRef?.dismiss();
    this._expensesSubscription?.unsubscribe();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this._getExpensesData();
  }
}
